import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import {
  ArrowLeft, Plus, Edit, Trash2, Search, Crown, Mail, Send, X,
  UserPlus, UserMinus, ChevronDown, ShieldCheck, ShieldX, Paperclip, Filter,
  CheckSquare, Square, Users as UsersIcon, Download, History,
  CheckCircle2, UserCheck as UserCheckIcon, UserX, LogIn, DollarSign, Clock,
  Link2, RefreshCw, Loader2, AtSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAMS = ['10u', '11u', '12u', '13u', '14u', '2026', '2027', '2028', '2029', '2030'];

const emptyPlayerForm = {
  name: '', photo_url: '', date_of_birth: '', height: '', weight: '',
  position: '', team: '10u', sixty_yard_time: '', home_to_first: '',
  velo: '', exit_velo: '', broad_jump: '', vertical_jump: '', school: ''
};

const PAGE_SIZE = 100;

const AdminPlayers = () => {
  const { isAuthenticated } = useAdmin();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, roster_total: 0, account_total: 0, premium_total: 0, per_team_account_count: {} });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);

  // Search & Filter
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [resendingPlayerId, setResendingPlayerId] = useState(null);

  // Roster edit form
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [prefillAccountId, setPrefillAccountId] = useState(null);
  const [formData, setFormData] = useState(emptyPlayerForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Team dropdown for unassigned accounts
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTeam, setEmailTeam] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', cc: '', bcc: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTeamDropdownOpen, setBulkTeamDropdownOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkTeamRef = useRef(null);
  const [isBulkEmail, setIsBulkEmail] = useState(false);

  // Activity log
  const [activityAccount, setActivityAccount] = useState(null);
  const [activityEntries, setActivityEntries] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Email recipient flags inside the email modal
  const [emailToPrimary, setEmailToPrimary] = useState(true);
  const [emailToSecondary, setEmailToSecondary] = useState(false);

  // Edit emails modal
  const [emailsEditAccount, setEmailsEditAccount] = useState(null);
  const [emailsEditForm, setEmailsEditForm] = useState({ email: '', secondary_email: '' });
  const [emailsEditSaving, setEmailsEditSaving] = useState(false);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!bulkTeamDropdownOpen) return;
    const h = (e) => {
      if (bulkTeamRef.current && !bulkTeamRef.current.contains(e.target)) {
        setBulkTeamDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [bulkTeamDropdownOpen]);

  // Debounce search input -> debouncedSearch
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side fetch (page 1) when filters change
  const fetchPage = async ({ append = false } = {}) => {
    if (!isAuthenticated) return;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (teamFilter && teamFilter !== 'all') params.append('team', teamFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (debouncedSearch.trim()) params.append('q', debouncedSearch.trim());
      params.append('limit', String(PAGE_SIZE));
      params.append('skip', String(append ? rows.length : 0));

      const res = await axios.get(`${API}/admin/players-list?${params.toString()}`);
      const data = res.data || {};
      setRows(append ? [...rows, ...(data.items || [])] : (data.items || []));
      setStats(data.stats || { total: 0, roster_total: 0, account_total: 0, premium_total: 0, per_team_account_count: {} });
      setHasMore(!!data.has_more);
    } catch (error) {
      console.error('players-list endpoint failed, falling back to legacy fetch:', error);
      // Graceful fallback for when the backend hasn't been redeployed yet (deploy window race),
      // or any other server-side issue with the new endpoint. We rebuild rows client-side from
      // the original three endpoints so the admin always sees their players.
      try {
        const [pRes, aRes, iRes] = await Promise.all([
          axios.get(`${API}/players?limit=1000`),
          axios.get(`${API}/admin/player-accounts`),
          axios.get(`${API}/admin/pending-invites`).catch(() => ({ data: [] })),
        ]);
        const fallbackRows = legacyMergeRows(pRes.data || [], aRes.data || [], iRes.data || []);
        const filtered = applyClientFilters(fallbackRows, { team: teamFilter, status: statusFilter, q: debouncedSearch });
        const perTeam = {};
        (aRes.data || []).forEach(a => {
          if (a?.team) perTeam[a.team] = (perTeam[a.team] || 0) + 1;
        });
        setRows(filtered);
        setStats({
          total: filtered.length,
          roster_total: filtered.filter(r => r.type === 'both' || r.type === 'roster').length,
          account_total: filtered.filter(r => r.account).length,
          premium_total: filtered.filter(r => r.isPremium).length,
          per_team_account_count: perTeam,
        });
        setHasMore(false);
        setMessage({ type: 'success', text: 'Loaded via legacy endpoint (backend deploy still rolling out)' });
      } catch (fallbackErr) {
        console.error('Legacy fallback also failed:', fallbackErr);
        setMessage({ type: 'error', text: 'Error loading players' });
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Refetch page 1 whenever filters change
  useEffect(() => {
    fetchPage({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, teamFilter, statusFilter, debouncedSearch]);

  const fetchAll = () => fetchPage({ append: false });

  const handleLoadMore = () => fetchPage({ append: true });

  // Server already filtered/sorted; treat rows as-is.
  const filteredRows = rows;
  // Accounts list derived from rows for the email-modal recipient list. (Bulk email uses ids; team-level
  // email uses backend-resolved recipients - so this only powers the success modal "X recipients" preview.)
  const accounts = useMemo(() => rows.filter(r => r.account).map(r => r.account), [rows]);
  const teamAccountCounts = stats.per_team_account_count || {};

  // ---------- Roster CRUD ----------
  const openNewPlayerForm = () => {
    setEditingPlayer(null);
    setPrefillAccountId(null);
    setFormData(emptyPlayerForm);
    setImagePreview(null);
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setPrefillAccountId(null);
    setFormData({
      name: player.name || '',
      photo_url: player.photo_url || '',
      date_of_birth: player.date_of_birth || '',
      height: player.height || '',
      weight: player.weight || '',
      position: player.position || '',
      team: player.team || '10u',
      sixty_yard_time: player.sixty_yard_time || '',
      home_to_first: player.home_to_first || '',
      velo: player.velo || '',
      exit_velo: player.exit_velo || '',
      broad_jump: player.broad_jump || '',
      vertical_jump: player.vertical_jump || '',
      school: player.school || ''
    });
    setImagePreview(player.photo_url);
    setSelectedFile(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPlayer(null);
    setPrefillAccountId(null);
    setFormData(emptyPlayerForm);
    setSelectedFile(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitPlayer = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = formData.photo_url;
      if (selectedFile) {
        setUploadingImage(true);
        const fd = new FormData();
        fd.append('file', selectedFile);
        const up = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        photoUrl = up.data.url;
        setUploadingImage(false);
      }
      const payload = { ...formData, photo_url: photoUrl };

      if (editingPlayer) {
        await axios.put(`${API}/players/${editingPlayer.id}`, payload);
        setMessage({ type: 'success', text: `${payload.name} updated` });
      } else if (prefillAccountId) {
        // Create roster card for an account
        const createRes = await axios.post(`${API}/players`, payload);
        const newPlayerId = createRes.data.id;
        // Link account to this player
        await axios.post(`${API}/admin/link-player-to-account?player_id=${newPlayerId}&account_id=${prefillAccountId}`);
        setMessage({ type: 'success', text: `Roster card created and linked to account` });
      } else {
        await axios.post(`${API}/players`, payload);
        setMessage({ type: 'success', text: `${payload.name} added to roster` });
      }
      await fetchAll();
      resetForm();
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error saving player' });
      setUploadingImage(false);
    }
  };

  const handleDeletePlayer = async (player) => {
    if (!window.confirm(`Delete roster card for ${player.name}? The login account (if any) will stay active.`)) return;
    try {
      await axios.delete(`${API}/players/${player.id}`);
      setMessage({ type: 'success', text: `${player.name} removed from roster` });
      fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting player' });
    }
  };

  // ---------- Account actions ----------
  const handleAddToTeam = async (accountId, team) => {
    setActionLoading(accountId);
    setDropdownOpen(null);
    try {
      await axios.post(`${API}/admin/add-account-to-team`, { account_id: accountId, team });
      setMessage({ type: 'success', text: `Added to ${team.toUpperCase()} team` });
      fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error adding to team' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveFromTeam = async (accountId) => {
    if (!window.confirm('Remove this player from team? The login account will remain active.')) return;
    setActionLoading(accountId);
    try {
      await axios.post(`${API}/admin/remove-account-from-team?account_id=${accountId}`);
      setMessage({ type: 'success', text: 'Removed from team' });
      fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error removing from team' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantPremium = async (accountId, name) => {
    if (!window.confirm(`Grant premium access to ${name}?`)) return;
    setActionLoading(accountId);
    try {
      await axios.post(`${API}/admin/player-accounts/${accountId}/grant-premium`);
      setMessage({ type: 'success', text: `Premium granted to ${name}` });
      fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error granting premium' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokePremium = async (accountId, name) => {
    if (!window.confirm(`Revoke premium access from ${name}?`)) return;
    setActionLoading(accountId);
    try {
      await axios.post(`${API}/admin/player-accounts/${accountId}/revoke-premium`);
      setMessage({ type: 'success', text: `Premium revoked from ${name}` });
      fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error revoking premium' });
    } finally {
      setActionLoading(null);
    }
  };

  // ---------- Bulk Selection ----------
  // Bulk selection only applies to rows that have a backing account
  const selectableRowIds = useMemo(() => filteredRows.filter(r => r.account).map(r => r.account.id), [filteredRows]);

  const toggleSelect = (accountId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId); else next.add(accountId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectableRowIds.every(id => selectedIds.has(id)) && selectableRowIds.length > 0) {
      // All selected -> clear
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableRowIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableRowIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedAccounts = useMemo(
    () => accounts.filter(a => selectedIds.has(a.id)),
    [accounts, selectedIds]
  );

  const openBulkEmailModal = () => {
    if (selectedIds.size === 0) return;
    setIsBulkEmail(true);
    setEmailRecipient(null);
    setEmailTeam(null);
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
    setEmailToPrimary(true); setEmailToSecondary(false);
    setShowEmailModal(true);
  };

  const handleBulkPremium = async (grant) => {
    if (selectedIds.size === 0) return;
    const actionLabel = grant ? 'granted' : 'revoked';
    if (!window.confirm(`${grant ? 'Grant' : 'Revoke'} premium for ${selectedIds.size} account(s)?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-premium`, {
        account_ids: Array.from(selectedIds),
        grant,
      });
      setMessage({ type: 'success', text: `Premium ${actionLabel} for ${res.data.modified} account(s)` });
      clearSelection();
      await fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || `Error updating premium` });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkMoveToTeam = async (team) => {
    if (selectedIds.size === 0) return;
    setBulkTeamDropdownOpen(false);
    if (!window.confirm(`Move ${selectedIds.size} player(s) to ${team.toUpperCase()}?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-move-to-team`, {
        account_ids: Array.from(selectedIds),
        team,
      });
      setMessage({ type: 'success', text: `Moved to ${team.toUpperCase()}: ${res.data.created} created, ${res.data.moved} updated` });
      clearSelection();
      await fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error moving players' });
    } finally {
      setBulkLoading(false);
    }
  };

  // ---------- Activity Log ----------
  const openEditEmails = (account) => {
    setEmailsEditAccount(account);
    setEmailsEditForm({
      email: account.email || '',
      secondary_email: account.secondary_email || '',
    });
  };

  const handleSaveEmails = async () => {
    if (!emailsEditAccount) return;
    setEmailsEditSaving(true);
    try {
      const payload = {};
      // Only include fields that actually changed
      if (emailsEditForm.email.trim() && emailsEditForm.email.trim() !== (emailsEditAccount.email || '')) {
        payload.email = emailsEditForm.email.trim();
      }
      // Always send secondary_email (including '' to clear) if user touched the field
      if ((emailsEditForm.secondary_email || '') !== (emailsEditAccount.secondary_email || '')) {
        payload.secondary_email = emailsEditForm.secondary_email.trim();
      }
      if (Object.keys(payload).length === 0) {
        setEmailsEditAccount(null);
        return;
      }
      const res = await axios.patch(`${API}/admin/player-accounts/${emailsEditAccount.id}/emails`, payload);
      setMessage({ type: 'success', text: `Emails updated for ${emailsEditAccount.name}` });
      setEmailsEditAccount(null);
      // Optimistic local row update
      setRows(prev => prev.map(r =>
        r.account && r.account.id === emailsEditAccount.id
          ? { ...r, email: res.data.email || r.email, account: { ...r.account, email: res.data.email, secondary_email: res.data.secondary_email } }
          : r
      ));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error updating emails' });
    } finally {
      setEmailsEditSaving(false);
    }
  };

  const handleResendInvite = async (playerId, optionalEmail) => {
    if (!window.confirm('Re-send the claim invite email and refresh the 14-day expiry?')) return;
    setResendingPlayerId(playerId);
    try {
      const url = `${API}/admin/players/${playerId}/resend-invite${optionalEmail ? `?email=${encodeURIComponent(optionalEmail)}` : ''}`;
      const res = await axios.post(url);
      setMessage({
        type: res.data.invite_sent ? 'success' : 'error',
        text: res.data.invite_sent
          ? `Invite re-sent to ${res.data.email}`
          : (res.data.message || 'Token refreshed but email failed to send'),
      });
      await fetchAll();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error resending invite' });
    } finally {
      setResendingPlayerId(null);
    }
  };

  const openActivityLog = async (account) => {
    setActivityAccount(account);
    setActivityEntries([]);
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/admin/activity-log/${account.id}`);
      setActivityEntries(res.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error loading activity' });
      setActivityAccount(null);
    } finally {
      setActivityLoading(false);
    }
  };

  // ---------- CSV Export ----------
  const handleExportCsv = async () => {
    if (stats.total === 0) {
      setMessage({ type: 'error', text: 'No players to export' });
      return;
    }
    // Fetch up to 1000 matching the CURRENT filters from the server so we export the full filtered set,
    // not just the loaded page.
    let exportRows = rows;
    try {
      const params = new URLSearchParams();
      if (teamFilter && teamFilter !== 'all') params.append('team', teamFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (debouncedSearch.trim()) params.append('q', debouncedSearch.trim());
      params.append('limit', '1000');
      params.append('skip', '0');
      const res = await axios.get(`${API}/admin/players-list?${params.toString()}`);
      exportRows = res.data?.items || rows;
    } catch (e) {
      // Fall back to currently loaded rows
      console.warn('CSV export full-fetch failed, using loaded rows', e);
    }
    const headers = [
      'Name', 'Email', 'Phone', 'Team', 'Position', 'School',
      'Row Type', 'Premium', 'Date of Birth',
      'Height', 'Weight', '60 Yard', 'Home to 1st',
      'OF/INF Velo', 'Exit Velo', 'Broad Jump', 'Vertical Jump'
    ];
    const rowTypeLabel = { both: 'Account + Roster', roster: 'Roster Only', account: 'Account Only' };
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    exportRows.forEach(r => {
      const p = r.player || {};
      const a = r.account || {};
      lines.push([
        r.name,
        r.email,
        a.phone || '',
        r.team ? r.team.toUpperCase() : '',
        r.position,
        r.school,
        rowTypeLabel[r.type] || r.type,
        r.account ? (r.isPremium ? 'Yes' : 'No') : '',
        p.date_of_birth || '',
        p.height || '',
        p.weight || '',
        p.sixty_yard_time || '',
        p.home_to_first || '',
        p.velo || '',
        p.exit_velo || '',
        p.broad_jump || '',
        p.vertical_jump || ''
      ].map(escape).join(','));
    });
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `pacific-ghost-players-${ts}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: `Exported ${exportRows.length} player(s) to CSV` });
  };

  // ---------- Email ----------
  const openTeamEmailModal = (team) => {
    setEmailTeam(team); setEmailRecipient(null);
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
    setEmailToPrimary(true); setEmailToSecondary(false);
    setShowEmailModal(true);
  };
  const openIndividualEmailModal = (account) => {
    setEmailRecipient(account); setEmailTeam(null);
    setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
    setEmailToPrimary(true);
    setEmailToSecondary(!!account.secondary_email);  // default ON if account has a secondary
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      setMessage({ type: 'error', text: 'Subject and message are required' });
      return;
    }
    if (!emailToPrimary && !emailToSecondary) {
      setMessage({ type: 'error', text: 'Select at least one recipient (primary or secondary)' });
      return;
    }
    setSendingEmail(true);
    try {
      let response, recipients = [];
      const flags = { to_primary: emailToPrimary, to_secondary: emailToSecondary };
      if (isBulkEmail) {
        response = await axios.post(`${API}/admin/send-bulk-email`, {
          account_ids: Array.from(selectedIds),
          subject: emailForm.subject,
          body: emailForm.body,
          cc: emailForm.cc.trim() || null,
          bcc: emailForm.bcc.trim() || null,
          ...flags,
        });
        recipients = selectedAccounts.flatMap(a => {
          const list = [];
          if (emailToPrimary && a.email) list.push({ name: a.name, email: a.email });
          if (emailToSecondary && a.secondary_email) list.push({ name: `${a.name} (2nd)`, email: a.secondary_email });
          return list;
        });
      } else if (emailRecipient) {
        response = await axios.post(`${API}/admin/send-individual-email`, {
          account_id: emailRecipient.id, subject: emailForm.subject, body: emailForm.body,
          cc: emailForm.cc.trim() || null, bcc: emailForm.bcc.trim() || null,
          ...flags,
        });
        if (emailToPrimary && emailRecipient.email) recipients.push({ name: emailRecipient.name, email: emailRecipient.email });
        if (emailToSecondary && emailRecipient.secondary_email) recipients.push({ name: `${emailRecipient.name} (2nd)`, email: emailRecipient.secondary_email });
      } else {
        response = await axios.post(`${API}/admin/send-team-email`, {
          team: emailTeam, subject: emailForm.subject, body: emailForm.body,
          cc: emailForm.cc.trim() || null, bcc: emailForm.bcc.trim() || null,
          ...flags,
        });
        recipients = accounts.filter(a => a.team === emailTeam).flatMap(a => {
          const list = [];
          if (emailToPrimary && a.email) list.push({ name: a.name, email: a.email });
          if (emailToSecondary && a.secondary_email) list.push({ name: `${a.name} (2nd)`, email: a.secondary_email });
          return list;
        });
      }
      if (emailForm.cc.trim()) recipients.push({ name: 'CC', email: emailForm.cc.trim() });
      if (emailForm.bcc.trim()) recipients.push({ name: 'BCC', email: emailForm.bcc.trim() });
      setEmailSuccess({
        subject: emailForm.subject, recipients,
        sent: response.data.sent || 1, failed: response.data.failed || 0,
        isTeam: !emailRecipient && !isBulkEmail, isBulk: isBulkEmail, teamName: emailTeam?.toUpperCase()
      });
      setShowEmailModal(false);
      setEmailForm({ subject: '', body: '', cc: '', bcc: '' });
      if (isBulkEmail) {
        setIsBulkEmail(false);
        clearSelection();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error sending email' });
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Access Denied</h1>
            <p>Please <Link to="/admin/login">login</Link> to access this page.</p>
          </div>
        </section>
      </div>
    );
  }

  const totalCount = stats.total;
  const accountCount = stats.account_total;
  const rosterCount = stats.roster_total;
  const premiumCount = stats.premium_total;

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Players</h1>
              <p className="body-large">Unified view of roster cards and login accounts</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={handleExportCsv}
                disabled={stats.total === 0 || loading}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                data-testid="export-csv-btn"
                title={`Download current view (${stats.total} player${stats.total !== 1 ? 's' : ''}) as CSV`}
              >
                <Download size={18} /> Export CSV
              </button>
              <button onClick={openNewPlayerForm} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="add-player-btn">
                <Plus size={20} /> Add Player
              </button>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Alert Message */}
          {message && (
            <div style={{
              padding: '12px 16px',
              background: message.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(220,38,38,0.2)',
              border: `1px solid ${message.type === 'success' ? '#22c55e' : '#dc2626'}`,
              borderRadius: '8px', marginBottom: '20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ color: message.type === 'success' ? '#22c55e' : '#ef4444' }}>{message.text}</span>
              <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1.25rem' }}>×</button>
            </div>
          )}

          {/* Quick Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Total', value: totalCount },
              { label: 'Roster Cards', value: rosterCount },
              { label: 'Accounts', value: accountCount },
              { label: 'Premium', value: premiumCount, color: 'var(--brand-primary)' }
            ].map(s => (
              <div key={s.label} className="network-card" style={{ padding: '14px 18px' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{s.label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color || 'inherit' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Send Email to Team */}
          <div className="network-card" style={{ marginBottom: '20px', padding: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <Mail size={20} style={{ color: 'var(--brand-primary)' }} />
              <h3 style={{ margin: 0 }}>Send Email to Team</h3>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {TEAMS.map(team => {
                const count = teamAccountCounts[team] || 0;
                return (
                  <button
                    key={team}
                    onClick={() => openTeamEmailModal(team)}
                    disabled={count === 0}
                    style={{
                      padding: '8px 14px',
                      background: count > 0 ? 'rgba(212,175,55,0.2)' : 'rgba(100,100,100,0.2)',
                      border: `1px solid ${count > 0 ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
                      borderRadius: '8px',
                      cursor: count > 0 ? 'pointer' : 'not-allowed',
                      color: count > 0 ? 'var(--text-primary)' : 'var(--text-light)',
                      display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem'
                    }}
                    data-testid={`email-team-${team}`}
                  >
                    <Send size={12} /> {team.toUpperCase()}
                    <span style={{
                      background: count > 0 ? 'var(--brand-primary)' : 'var(--border-medium)',
                      color: count > 0 ? 'black' : 'var(--text-light)',
                      padding: '1px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600
                    }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + Filters */}
          <div className="network-card" style={{ marginBottom: '20px', padding: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
              <Search size={16} style={{ position: 'absolute', top: '50%', left: '12px', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, school, position..."
                style={{
                  width: '100%', padding: '10px 12px 10px 38px',
                  background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)', borderRadius: '8px'
                }}
                data-testid="players-search-input"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Filter size={14} style={{ color: 'var(--text-light)' }} />
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', border: '1px solid var(--border-medium)', borderRadius: '8px' }}
                data-testid="players-team-filter"
              >
                <option value="all">All Teams</option>
                {TEAMS.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { v: 'all', l: 'All' },
                { v: 'both', l: 'Account + Roster' },
                { v: 'account-only', l: 'Account Only' },
                { v: 'roster-only', l: 'Roster Only' },
                { v: 'premium', l: 'Premium' },
                { v: 'free', l: 'Free' },
              ].map(s => (
                <button
                  key={s.v}
                  onClick={() => setStatusFilter(s.v)}
                  data-testid={`status-filter-${s.v}`}
                  style={{
                    padding: '7px 12px',
                    background: statusFilter === s.v ? 'var(--brand-primary)' : 'rgba(255,255,255,0.06)',
                    color: statusFilter === s.v ? 'black' : 'var(--text-primary)',
                    border: '1px solid ' + (statusFilter === s.v ? 'var(--brand-primary)' : 'var(--border-medium)'),
                    borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer'
                  }}
                >{s.l}</button>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', color: 'var(--text-light)', fontSize: '0.85rem' }} data-testid="players-result-count">
              Showing {rows.length} of {stats.total} result{stats.total !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Roster Form */}
          {showForm && (
            <div className="network-card" style={{ marginBottom: '24px', padding: '24px' }}>
              <h2 className="heading-2" style={{ marginTop: 0 }}>
                {editingPlayer ? `Edit Roster Card: ${editingPlayer.name}` : (prefillAccountId ? 'Create Roster Card for Account' : 'Add New Player')}
              </h2>
              <form onSubmit={handleSubmitPlayer} style={{ marginTop: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                  <div className="form-group"><label>Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                  <div className="form-group"><label>Date of Birth</label><input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} /></div>
                  <div className="form-group"><label>Team *</label>
                    <select value={formData.team} onChange={(e) => setFormData({ ...formData, team: e.target.value })} required>
                      {TEAMS.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Position *</label><input type="text" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required placeholder="e.g., SS/2B" /></div>
                  <div className="form-group"><label>Height *</label><input type="text" value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })} required placeholder="5'10&quot;" /></div>
                  <div className="form-group"><label>Weight *</label><input type="text" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} required placeholder="165 lbs" /></div>
                  <div className="form-group"><label>60 Yard Time *</label><input type="text" value={formData.sixty_yard_time} onChange={(e) => setFormData({ ...formData, sixty_yard_time: e.target.value })} required placeholder="7.2s" /></div>
                  <div className="form-group"><label>Home to 1st *</label><input type="text" value={formData.home_to_first} onChange={(e) => setFormData({ ...formData, home_to_first: e.target.value })} required placeholder="4.5s" /></div>
                  <div className="form-group"><label>OF/INF Velo *</label><input type="text" value={formData.velo} onChange={(e) => setFormData({ ...formData, velo: e.target.value })} required placeholder="75 mph" /></div>
                  <div className="form-group"><label>Exit Velo *</label><input type="text" value={formData.exit_velo} onChange={(e) => setFormData({ ...formData, exit_velo: e.target.value })} required placeholder="85 mph" /></div>
                  <div className="form-group"><label>Broad Jump *</label><input type="text" value={formData.broad_jump} onChange={(e) => setFormData({ ...formData, broad_jump: e.target.value })} required placeholder="8'2&quot;" /></div>
                  <div className="form-group"><label>Vertical Jump *</label><input type="text" value={formData.vertical_jump} onChange={(e) => setFormData({ ...formData, vertical_jump: e.target.value })} required placeholder='28"' /></div>
                  <div className="form-group"><label>School</label><input type="text" value={formData.school} onChange={(e) => setFormData({ ...formData, school: e.target.value })} placeholder="High School name" /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Player Photo</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} style={{ marginBottom: '10px' }} />
                    {imagePreview && <div style={{ marginTop: '10px' }}><img src={imagePreview} alt="" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--border-medium)' }} /></div>}
                  </div>
                </div>
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                  <button type="submit" className="btn-primary" disabled={uploadingImage}>{uploadingImage ? 'Uploading...' : (editingPlayer ? 'Update Player' : 'Save Player')}</button>
                  <button type="button" onClick={resetForm} className="btn-secondary" disabled={uploadingImage}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Unified Table */}
          {loading ? (
            <p>Loading...</p>
          ) : filteredRows.length === 0 ? (
            <div className="network-card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-light)' }}>No players match the current filters.</p>
            </div>
          ) : (
            <div className="network-card" style={{ overflow: 'visible' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <th style={{ ...thStyle, width: '40px', paddingRight: 0 }}>
                        <button
                          type="button"
                          onClick={toggleSelectAll}
                          title={selectableRowIds.length > 0 && selectableRowIds.every(id => selectedIds.has(id)) ? 'Deselect all' : 'Select all (with account)'}
                          disabled={selectableRowIds.length === 0}
                          style={{ background: 'none', border: 'none', cursor: selectableRowIds.length === 0 ? 'not-allowed' : 'pointer', color: 'var(--brand-primary)', padding: '2px', opacity: selectableRowIds.length === 0 ? 0.4 : 1 }}
                          data-testid="select-all-checkbox"
                        >
                          {selectableRowIds.length > 0 && selectableRowIds.every(id => selectedIds.has(id))
                            ? <CheckSquare size={18} />
                            : <Square size={18} />}
                        </button>
                      </th>
                      <th style={thStyle}>Player</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Team</th>
                      <th style={thStyle}>Position</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(row => {
                      const canSelect = !!row.account;
                      const isSelected = canSelect && selectedIds.has(row.account.id);
                      return (
                      <tr
                        key={row.key}
                        style={{
                          borderBottom: '1px solid var(--border-light)',
                          background: isSelected ? 'rgba(212,175,55,0.08)' : 'transparent'
                        }}
                        data-testid={`player-row-${row.key}`}
                      >
                        <td style={{ ...tdStyle, paddingRight: 0 }}>
                          {canSelect ? (
                            <button
                              type="button"
                              onClick={() => toggleSelect(row.account.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: isSelected ? 'var(--brand-primary)' : 'var(--text-light)', padding: '2px' }}
                              data-testid={`row-checkbox-${row.account.id}`}
                              aria-label={isSelected ? 'Deselect row' : 'Select row'}
                            >
                              {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                            </button>
                          ) : (
                            <span style={{ color: '#555' }} title="No account to select">—</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {(() => {
                              const ringStyle = row.isPremium ? {
                                border: '2px solid var(--brand-primary)',
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.4), 0 0 8px rgba(212,175,55,0.55)',
                              } : { border: '2px solid transparent' };
                              return row.photo_url ? (
                                <img
                                  data-testid={row.isPremium ? `premium-ring-${row.account?.id || row.player?.id}` : undefined}
                                  src={row.photo_url}
                                  alt=""
                                  title={row.isPremium ? 'Premium member' : undefined}
                                  style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', ...ringStyle }}
                                />
                              ) : (
                                <div
                                  data-testid={row.isPremium ? `premium-ring-${row.account?.id || row.player?.id}` : undefined}
                                  title={row.isPremium ? 'Premium member' : undefined}
                                  style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)', fontWeight: 600, ...ringStyle }}
                                >
                                  {row.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              );
                            })()}
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>
                                {row.name}
                                {row.isPremium && <Crown size={14} style={{ color: 'var(--brand-primary)' }} />}
                              </div>
                              {row.school && <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{row.school}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-light)' }}>{row.email || '—'}</td>
                        <td style={tdStyle}>
                          {row.team ? (
                            <span style={{ padding: '3px 10px', background: 'rgba(34,197,94,0.2)', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, color: '#22c55e' }}>
                              {row.team.toUpperCase()}
                            </span>
                          ) : <span style={{ color: 'var(--text-light)', fontStyle: 'italic', fontSize: '0.85rem' }}>Unassigned</span>}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-light)' }}>{row.position || '—'}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                              background: row.type === 'both' ? 'rgba(59,130,246,0.2)' : row.type === 'roster' ? 'rgba(168,85,247,0.2)' : 'rgba(251,191,36,0.2)',
                              color: row.type === 'both' ? '#60a5fa' : row.type === 'roster' ? '#c084fc' : '#fbbf24',
                              width: 'fit-content'
                            }}>
                              {row.type === 'both' ? 'ACCOUNT + ROSTER' : row.type === 'roster' ? 'ROSTER ONLY' : 'ACCOUNT ONLY'}
                            </span>
                            {row.account && (
                              <span style={{
                                padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                                background: row.isPremium ? 'rgba(212,175,55,0.2)' : 'rgba(100,100,100,0.3)',
                                color: row.isPremium ? 'var(--brand-primary)' : 'var(--text-light)',
                                width: 'fit-content'
                              }}>
                                {row.isPremium ? 'PREMIUM' : 'FREE'}
                              </span>
                            )}
                            {row.invite && (
                              <span title={`Sent to ${row.invite.email}`} data-testid={`invite-chip-${row.player.id}`} style={{
                                padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                                background: 'rgba(59,130,246,0.18)', color: '#60a5fa',
                                width: 'fit-content', display: 'inline-flex', alignItems: 'center', gap: '4px'
                              }}>
                                <Link2 size={11} /> {formatInviteChip(row.invite)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {row.player && (
                              <button onClick={() => handleEditPlayer(row.player)} title="Edit roster card" style={iconBtn('#d4af37')} data-testid={`edit-player-${row.player.id}`}>
                                <Edit size={14} /> Edit
                              </button>
                            )}
                            {row.invite && (
                              <button
                                onClick={() => handleResendInvite(row.player.id)}
                                disabled={resendingPlayerId === row.player.id}
                                title={`Resend invite to ${row.invite.email}`}
                                style={iconBtn('#3b82f6')}
                                data-testid={`resend-invite-${row.player.id}`}
                              >
                                <RefreshCw size={14} className={resendingPlayerId === row.player.id ? 'animate-spin' : ''} />
                                {resendingPlayerId === row.player.id ? 'Sending' : 'Resend'}
                              </button>
                            )}
                            {row.account && (
                              <button onClick={() => openIndividualEmailModal(row.account)} title="Send email" style={iconBtn('#3b82f6')} data-testid={`email-${row.account.id}`}>
                                <Mail size={14} /> Email
                              </button>
                            )}
                            {row.account && (
                              <button onClick={() => openEditEmails(row.account)} title="Edit email addresses" style={iconBtn('#06b6d4')} data-testid={`edit-emails-${row.account.id}`}>
                                <AtSign size={14} /> Emails
                              </button>
                            )}
                            {row.account && (
                              <button onClick={() => openActivityLog(row.account)} title="View activity history" style={iconBtn('#a855f7')} data-testid={`history-${row.account.id}`}>
                                <History size={14} /> History
                              </button>
                            )}
                            {row.account && row.isPremium && (
                              <button onClick={() => handleRevokePremium(row.account.id, row.account.name)} disabled={actionLoading === row.account.id} title="Revoke premium" style={iconBtn('#ef4444')} data-testid={`revoke-premium-${row.account.id}`}>
                                <ShieldX size={14} /> Revoke
                              </button>
                            )}
                            {row.account && !row.isPremium && (
                              <button onClick={() => handleGrantPremium(row.account.id, row.account.name)} disabled={actionLoading === row.account.id} title="Grant premium" style={iconBtn('#22c55e')} data-testid={`grant-premium-${row.account.id}`}>
                                <ShieldCheck size={14} /> Premium
                              </button>
                            )}
                            {row.type === 'account' && (
                              <div ref={dropdownOpen === row.account.id ? dropdownRef : null} style={{ position: 'relative', display: 'inline-block', zIndex: dropdownOpen === row.account.id ? 1000 : 1 }}>
                                <button
                                  onClick={() => setDropdownOpen(dropdownOpen === row.account.id ? null : row.account.id)}
                                  disabled={actionLoading === row.account.id}
                                  style={iconBtn('#d4af37', true)}
                                  data-testid={`add-btn-${row.account.id}`}
                                >
                                  <UserPlus size={14} /> Add to Team <ChevronDown size={14} />
                                </button>
                                {dropdownOpen === row.account.id && (
                                  <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: '4px',
                                    background: '#1a1a1a', border: '1px solid #444', borderRadius: '8px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.7)', zIndex: 9999,
                                    minWidth: '180px', maxHeight: '320px', overflowY: 'auto'
                                  }}>
                                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', fontSize: '0.7rem', color: '#999', fontWeight: 600, position: 'sticky', top: 0, background: '#1a1a1a' }}>
                                      Select Team
                                    </div>
                                    <div style={{ padding: '4px 8px', fontSize: '0.65rem', color: '#666', borderBottom: '1px solid #333' }}>Youth</div>
                                    {TEAMS.filter(t => t.includes('u')).map(t => (
                                      <button key={t} onClick={() => handleAddToTeam(row.account.id, t)} style={menuItemStyle} data-testid={`team-option-${t}`}
                                        onMouseEnter={(e) => e.target.style.background = 'rgba(212,175,55,0.2)'}
                                        onMouseLeave={(e) => e.target.style.background = 'none'}>
                                        {t.toUpperCase()}
                                      </button>
                                    ))}
                                    <div style={{ padding: '4px 8px', fontSize: '0.65rem', color: '#666', borderBottom: '1px solid #333', borderTop: '1px solid #333' }}>High School</div>
                                    {TEAMS.filter(t => !t.includes('u')).map(t => (
                                      <button key={t} onClick={() => handleAddToTeam(row.account.id, t)} style={menuItemStyle} data-testid={`team-option-${t}`}
                                        onMouseEnter={(e) => e.target.style.background = 'rgba(212,175,55,0.2)'}
                                        onMouseLeave={(e) => e.target.style.background = 'none'}>
                                        {t}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {row.type === 'both' && (
                              <button onClick={() => handleRemoveFromTeam(row.account.id)} disabled={actionLoading === row.account.id} title="Remove from team (keeps account)" style={iconBtn('#ef4444')} data-testid={`remove-btn-${row.account.id}`}>
                                <UserMinus size={14} /> Remove
                              </button>
                            )}
                            {row.player && (
                              <button onClick={() => handleDeletePlayer(row.player)} title="Delete roster card" style={iconBtn('#ef4444')} data-testid={`delete-player-${row.player.id}`}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
              {hasMore && (
                <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid var(--border-light)' }}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="btn-secondary"
                    style={{ padding: '10px 24px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? <Loader2 size={16} className="animate-spin" /> : <ChevronDown size={16} />}
                    {loadingMore ? 'Loading...' : `Load more (${stats.total - rows.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Sticky Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bulk-actions-toolbar"
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#111',
            border: '1px solid var(--brand-primary)',
            borderRadius: '14px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.7)',
            zIndex: 900,
            maxWidth: 'calc(100vw - 40px)',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '10px', borderRight: '1px solid #333' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              background: 'var(--brand-primary)', color: 'black',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.9rem'
            }}>{selectedIds.size}</div>
            <span style={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>selected</span>
          </div>
          <button
            onClick={openBulkEmailModal}
            disabled={bulkLoading}
            style={bulkBtn('#3b82f6')}
            data-testid="bulk-email-btn"
          >
            <Mail size={14} /> Email
          </button>
          <button
            onClick={() => handleBulkPremium(true)}
            disabled={bulkLoading}
            style={bulkBtn('#22c55e')}
            data-testid="bulk-grant-premium-btn"
          >
            <ShieldCheck size={14} /> Grant Premium
          </button>
          <button
            onClick={() => handleBulkPremium(false)}
            disabled={bulkLoading}
            style={bulkBtn('#ef4444')}
            data-testid="bulk-revoke-premium-btn"
          >
            <ShieldX size={14} /> Revoke Premium
          </button>
          <div ref={bulkTeamRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setBulkTeamDropdownOpen(v => !v)}
              disabled={bulkLoading}
              style={bulkBtn('#d4af37')}
              data-testid="bulk-move-team-btn"
            >
              <UsersIcon size={14} /> Move to Team <ChevronDown size={12} />
            </button>
            {bulkTeamDropdownOpen && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', right: 0,
                background: '#1a1a1a', border: '1px solid #444', borderRadius: '8px',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.7)', zIndex: 9999,
                minWidth: '180px', maxHeight: '280px', overflowY: 'auto'
              }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', fontSize: '0.7rem', color: '#999', fontWeight: 600, position: 'sticky', top: 0, background: '#1a1a1a' }}>
                  Select Team
                </div>
                <div style={{ padding: '4px 8px', fontSize: '0.65rem', color: '#666', borderBottom: '1px solid #333' }}>Youth</div>
                {TEAMS.filter(t => t.includes('u')).map(t => (
                  <button
                    key={t}
                    onClick={() => handleBulkMoveToTeam(t)}
                    style={menuItemStyle}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(212,175,55,0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'none'}
                    data-testid={`bulk-team-option-${t}`}
                  >{t.toUpperCase()}</button>
                ))}
                <div style={{ padding: '4px 8px', fontSize: '0.65rem', color: '#666', borderBottom: '1px solid #333', borderTop: '1px solid #333' }}>High School</div>
                {TEAMS.filter(t => !t.includes('u')).map(t => (
                  <button
                    key={t}
                    onClick={() => handleBulkMoveToTeam(t)}
                    style={menuItemStyle}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(212,175,55,0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'none'}
                    data-testid={`bulk-team-option-${t}`}
                  >{t}</button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={clearSelection}
            disabled={bulkLoading}
            style={{ ...bulkBtn('#999'), marginLeft: '4px' }}
            data-testid="bulk-clear-btn"
          >
            <X size={14} /> Clear
          </button>
        </div>
      )}

      {/* Edit Emails Modal */}
      {emailsEditAccount && (
        <div style={modalBackdrop} data-testid="edit-emails-modal">
          <div style={{ ...modalCard, maxWidth: '480px' }}>
            <button onClick={() => setEmailsEditAccount(null)} style={modalCloseBtn}><X size={22} /></button>
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <AtSign size={24} style={{ color: '#06b6d4' }} />
                <h2 style={{ margin: 0, color: '#fff' }}>Edit Email Addresses</h2>
              </div>
              <p style={{ margin: 0, color: '#999', fontSize: '0.9rem' }}>{emailsEditAccount.name}</p>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={modalLabel}>Primary email *</label>
              <input
                type="email"
                value={emailsEditForm.email}
                onChange={(e) => setEmailsEditForm({ ...emailsEditForm, email: e.target.value })}
                style={modalInput}
                data-testid="edit-emails-primary"
              />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>Used for login and as the default recipient.</div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={modalLabel}>Secondary email (optional)</label>
              <input
                type="email"
                value={emailsEditForm.secondary_email}
                onChange={(e) => setEmailsEditForm({ ...emailsEditForm, secondary_email: e.target.value })}
                placeholder="e.g., parent@example.com"
                style={modalInput}
                data-testid="edit-emails-secondary"
              />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>Leave blank to remove. Used only when you opt in via the email modal.</div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSaveEmails}
                disabled={emailsEditSaving || !emailsEditForm.email.trim()}
                style={{
                  flex: 1, padding: '12px',
                  background: emailsEditSaving ? '#666' : 'var(--brand-primary)',
                  color: 'black', border: 'none', borderRadius: '8px',
                  fontWeight: 700, cursor: emailsEditSaving ? 'wait' : 'pointer',
                }}
                data-testid="edit-emails-save"
              >
                {emailsEditSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEmailsEditAccount(null)}
                style={{ padding: '12px 20px', background: 'transparent', color: '#999', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer' }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {activityAccount && (
        <div style={modalBackdrop} data-testid="activity-log-modal">
          <div style={{ ...modalCard, maxWidth: '640px' }}>
            <button onClick={() => setActivityAccount(null)} style={modalCloseBtn} data-testid="activity-log-close"><X size={22} /></button>
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <History size={24} style={{ color: '#a855f7' }} />
                <h2 style={{ margin: 0, color: '#fff' }}>Activity Log</h2>
              </div>
              <p style={{ margin: 0, color: '#999', fontSize: '0.9rem' }}>
                {activityAccount.name} &middot; {activityAccount.email}
              </p>
            </div>
            {activityLoading ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>Loading activity...</p>
            ) : activityEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <Clock size={32} style={{ opacity: 0.4, marginBottom: '10px' }} />
                <p style={{ margin: 0 }}>No activity recorded yet.</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#666' }}>
                  Actions will appear here as they happen (emails, premium changes, team moves, payments).
                </p>
              </div>
            ) : (
              <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
                {activityEntries.map((entry) => {
                  const meta = eventMeta(entry.event_type);
                  const Icon = meta.Icon;
                  return (
                    <div key={entry.id} style={{ display: 'flex', gap: '12px', paddingBottom: '14px', marginBottom: '14px', borderBottom: '1px solid #2a2a2a' }} data-testid="activity-entry">
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                        background: `${meta.color}22`, border: `1px solid ${meta.color}66`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Icon size={16} style={{ color: meta.color }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontWeight: 500, fontSize: '0.92rem' }}>{entry.description}</div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px', fontSize: '0.75rem', color: '#888' }}>
                          <span>{formatActivityTimestamp(entry.created_at)}</span>
                          <span style={{ color: '#555' }}>&middot;</span>
                          <span style={{ textTransform: 'capitalize', color: meta.color }}>{entry.actor || 'system'}</span>
                          {entry.metadata?.channel && (
                            <>
                              <span style={{ color: '#555' }}>&middot;</span>
                              <span style={{ color: '#888' }}>{entry.metadata.channel}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setActivityAccount(null)} style={{ padding: '10px 20px', background: 'transparent', color: '#999', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <button onClick={() => { setShowEmailModal(false); setIsBulkEmail(false); }} style={modalCloseBtn}><X size={22} /></button>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <Mail size={24} style={{ color: isBulkEmail ? '#a855f7' : (emailRecipient ? '#3b82f6' : 'var(--brand-primary)') }} />
                <h2 style={{ margin: 0, color: '#fff' }}>
                  {isBulkEmail
                    ? `Send Email to ${selectedIds.size} Selected Player(s)`
                    : (emailRecipient ? `Send Email to ${emailRecipient.name}` : `Send Email to ${emailTeam?.toUpperCase()} Team`)}
                </h2>
              </div>
              <p style={{ margin: 0, color: '#999', fontSize: '0.9rem' }}>
                {isBulkEmail
                  ? `Each selected player will receive a personalized copy.`
                  : (emailRecipient ? `To: ${emailRecipient.email}` : `To: all ${accounts.filter(a => a.team === emailTeam).length} player(s) on the ${emailTeam?.toUpperCase()} team`)}
              </p>
            </div>
            {/* Recipient selector (primary / secondary) */}
            <div style={{ marginBottom: '16px', padding: '10px 14px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', color: '#999', marginBottom: '6px' }}>Send to:</div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '18px', color: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={emailToPrimary} onChange={(e) => setEmailToPrimary(e.target.checked)} data-testid="email-to-primary" />
                Primary email
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={emailToSecondary} onChange={(e) => setEmailToSecondary(e.target.checked)} data-testid="email-to-secondary" />
                Secondary email
              </label>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>
                Recipients without that field are silently skipped.
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={modalLabel}>Subject *</label>
              <input type="text" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} style={modalInput} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={modalLabel}>Message *</label>
              <textarea rows={8} value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} style={{ ...modalInput, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div><label style={modalLabel}>CC</label><input type="email" value={emailForm.cc} onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })} style={modalInput} /></div>
              <div><label style={modalLabel}>BCC</label><input type="email" value={emailForm.bcc} onChange={(e) => setEmailForm({ ...emailForm, bcc: e.target.value })} style={modalInput} /></div>
            </div>
            <div style={{ padding: '10px 14px', background: 'rgba(212,175,55,0.1)', borderRadius: '8px', marginBottom: '16px', display: 'flex', gap: '10px' }}>
              <Paperclip size={16} style={{ color: 'var(--brand-primary)', marginTop: '2px' }} />
              <div style={{ fontSize: '0.8rem', color: '#ccc' }}>Paste URLs directly in the message for links/attachments.</div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleSendEmail} disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.body.trim()} style={{ flex: 1, padding: '12px', background: sendingEmail ? '#666' : 'var(--brand-primary)', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: sendingEmail ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Send size={16} /> {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
              <button onClick={() => { setShowEmailModal(false); setIsBulkEmail(false); }} style={{ padding: '12px 20px', background: 'transparent', color: '#999', border: '1px solid #444', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Success Popup */}
      {emailSuccess && (
        <div style={{ ...modalBackdrop, zIndex: 1002 }}>
          <div style={{ ...modalCard, border: '2px solid #22c55e', maxWidth: '500px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Send size={28} style={{ color: '#22c55e' }} />
            </div>
            <h2 style={{ color: '#22c55e', marginBottom: '6px' }}>Email Sent!</h2>
            <p style={{ color: '#ccc', marginBottom: '16px' }}>
              {emailSuccess.isTeam ? `Your email was sent to the ${emailSuccess.teamName} team` : 'Your email was sent successfully'}
            </p>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '14px', marginBottom: '16px', textAlign: 'left' }}>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#999', fontSize: '0.8rem' }}>Subject:</span>
                <p style={{ color: '#fff', margin: '2px 0 0', fontWeight: 500 }}>{emailSuccess.subject}</p>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <span style={{ color: '#999', fontSize: '0.8rem' }}>Sent to:</span>
                <p style={{ color: '#22c55e', margin: '2px 0 0', fontWeight: 600 }}>{emailSuccess.sent} recipient{emailSuccess.sent !== 1 ? 's' : ''}</p>
              </div>
              {emailSuccess.recipients?.length > 0 && (
                <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                  {emailSuccess.recipients.slice(0, 10).map((r, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #333', fontSize: '0.85rem' }}>
                      <span style={{ color: '#fff' }}>{r.name}</span>
                      <span style={{ color: '#666', marginLeft: '6px' }}>({r.email})</span>
                    </div>
                  ))}
                  {emailSuccess.recipients.length > 10 && <p style={{ color: '#999', fontSize: '0.8rem', marginTop: '6px' }}>+{emailSuccess.recipients.length - 10} more</p>}
                </div>
              )}
              {emailSuccess.failed > 0 && <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(239,68,68,0.2)', borderRadius: '4px' }}><span style={{ color: '#ef4444', fontSize: '0.8rem' }}>{emailSuccess.failed} failed</span></div>}
            </div>
            <button onClick={() => setEmailSuccess(null)} style={{ padding: '10px 28px', background: '#22c55e', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline style helpers
const thStyle = { padding: '12px 14px', textAlign: 'left', borderBottom: '1px solid var(--border-medium)', fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-light)', fontWeight: 600 };
const tdStyle = { padding: '12px 14px', fontSize: '0.9rem' };
const iconBtn = (color, filled = false) => ({
  padding: '5px 10px',
  background: filled ? color : `${color}22`,
  border: `1px solid ${filled ? color : `${color}66`}`,
  borderRadius: '6px', cursor: 'pointer',
  color: filled ? 'black' : color,
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  fontSize: '0.75rem', fontWeight: 600
});
const bulkBtn = (color) => ({
  padding: '8px 14px',
  background: `${color}22`,
  border: `1px solid ${color}88`,
  borderRadius: '8px', cursor: 'pointer',
  color,
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  fontSize: '0.85rem', fontWeight: 600,
  transition: 'background 0.15s'
});
const menuItemStyle = {
  display: 'block', width: '100%', padding: '9px 12px',
  background: 'none', border: 'none', textAlign: 'left',
  cursor: 'pointer', color: '#e5e5e5', fontSize: '0.85rem', transition: 'background 0.15s'
};
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px' };
const modalCard = { background: '#1a1a1a', borderRadius: '14px', padding: '28px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', border: '1px solid var(--border-medium)' };
const modalCloseBtn = { position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', cursor: 'pointer', color: '#999' };
const modalLabel = { display: 'block', marginBottom: '6px', color: '#ccc', fontWeight: 500, fontSize: '0.85rem' };
const modalInput = { width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: '#fff', fontSize: '0.95rem' };

const EVENT_META = {
  account_created: { color: '#3b82f6', Icon: LogIn },
  account_claimed: { color: '#22c55e', Icon: UserCheckIcon },
  team_assigned: { color: '#d4af37', Icon: UsersIcon },
  team_removed: { color: '#ef4444', Icon: UserX },
  premium_granted: { color: '#22c55e', Icon: ShieldCheck },
  premium_revoked: { color: '#ef4444', Icon: ShieldX },
  email_sent: { color: '#3b82f6', Icon: Mail },
  summer_program_enrolled: { color: '#d4af37', Icon: DollarSign },
  showcase_registered: { color: '#a855f7', Icon: CheckCircle2 },
};
const eventMeta = (type) => EVENT_META[type] || { color: '#999', Icon: Clock };

const formatActivityTimestamp = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return ts;
  }
};

const formatInviteChip = (invite) => {
  if (!invite?.expires_at) return 'Invite pending';
  try {
    const expires = new Date(invite.expires_at);
    const now = new Date();
    const msLeft = expires - now;
    if (msLeft <= 0) return 'Invite expired';
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    return `Invite · ${daysLeft}d left`;
  } catch {
    return 'Invite pending';
  }
};

// Legacy fallback helpers — used only when the new /admin/players-list endpoint is unavailable
// (e.g., during a deployment window where backend lags frontend).
const legacyMergeRows = (players, accounts, pendingInvites) => {
  const accountByPlayerId = {};
  const accountById = {};
  accounts.forEach(a => {
    accountById[a.id] = a;
    if (a.linked_player_id) accountByPlayerId[a.linked_player_id] = a;
  });
  const inviteByPlayerId = {};
  pendingInvites.forEach(i => { inviteByPlayerId[i.player_id] = i; });

  const rows = [];
  players.forEach(p => {
    const linkedAccount = accountByPlayerId[p.id] || (p.claimed_by_account_id ? accountById[p.claimed_by_account_id] : null);
    rows.push({
      key: `p-${p.id}`,
      type: linkedAccount ? 'both' : 'roster',
      player: p,
      account: linkedAccount,
      invite: !linkedAccount ? (inviteByPlayerId[p.id] || null) : null,
      name: p.name,
      email: linkedAccount?.email || (inviteByPlayerId[p.id]?.email) || '',
      team: p.team,
      position: p.position,
      school: p.school || '',
      photo_url: p.photo_url,
      isPremium: !!linkedAccount?.is_premium,
    });
  });
  accounts.forEach(a => {
    if (a.linked_player_id) return;
    rows.push({
      key: `a-${a.id}`,
      type: 'account',
      player: null,
      account: a,
      invite: null,
      name: a.name,
      email: a.email,
      team: a.team || '',
      position: '',
      school: '',
      photo_url: null,
      isPremium: !!a.is_premium,
    });
  });
  return rows;
};

const applyClientFilters = (rows, { team, status, q }) => {
  return rows.filter(r => {
    if (team && team !== 'all' && r.team !== team) return false;
    if (status === 'account-only' && r.type !== 'account') return false;
    if (status === 'roster-only' && r.type !== 'roster') return false;
    if (status === 'both' && r.type !== 'both') return false;
    if (status === 'premium' && !r.isPremium) return false;
    if (status === 'free' && (r.isPremium || !r.account)) return false;
    if (q && q.trim()) {
      const ql = q.trim().toLowerCase();
      const hay = `${r.name || ''} ${r.email || ''} ${r.school || ''} ${r.position || ''}`.toLowerCase();
      if (!hay.includes(ql)) return false;
    }
    return true;
  });
};

export default AdminPlayers;
